"use client";

import {
  Network,
  BrainCircuit,
  Layers,
  Globe2,
  ShieldCheck,
  Rocket,
  BadgeCheck,
  Clock,
  PhoneCall,
  FileText,
  Users,
  Trophy,
  Banknote,
  TrendingUp,
  HeartHandshake,
  type LucideIcon,
} from "@/components/icons";
import { STATS } from "@/lib/constants";

export type WhyItem = {
  icon: LucideIcon;
  title: string;
  description: string;
};

export const whyItems: WhyItem[] = [
  {
    icon: Network,
    title: "Real infrastructure at scale",
    description:
      "You won't spend your days tweaking UI components. The work here is protocols, routing, and networking layers that power millions of daily proxy requests.",
  },
  {
    icon: BrainCircuit,
    title: "Hard problems",
    description: `IP rotation strategies, latency optimization across ${STATS.countries} countries, network-level targeting. The problems are specific and technical, and the solutions you build have measurable impact.`,
  },
  {
    icon: Rocket,
    title: "High ownership",
    description:
      "No six-layer approval processes. Engineers own features end to end and ship to production weekly. Your ideas go live and you see the results.",
  },
  {
    icon: Layers,
    title: "Full-stack visibility",
    description:
      "Whether you're in backend, infra, or product, you'll understand how everything connects, from a customer's API call to the mobile device delivering the IP.",
  },
  {
    icon: Globe2,
    title: "Small team, big surface area",
    description:
      "A small team means you touch more of the product than you would at a larger company. Less bureaucracy, more direct impact on what ships.",
  },
  {
    icon: ShieldCheck,
    title: "Mission that matters",
    description:
      "Proxidize exists to democratize data access. Every person here is directly contributing to that, not shipping features for vanity metrics.",
  },
];

export type HiringStep = {
  icon: LucideIcon;
  step: string;
  title: string;
  duration: string;
  description: string;
};

export const hiringSteps: HiringStep[] = [
  {
    icon: PhoneCall,
    step: "01",
    title: "Intro call",
    duration: "30 min",
    description:
      "A relaxed conversation about your background, what you're looking for, and a high-level overview of the role and the team.",
  },
  {
    icon: FileText,
    step: "02",
    title: "Technical screen",
    duration: "60–90 min",
    description:
      "A focused technical assessment relevant to the role. No trick questions, no whiteboard theater. We care about how you think through real problems.",
  },
  {
    icon: Users,
    step: "03",
    title: "Team interviews",
    duration: "2–3 sessions",
    description:
      "Meet the people you'd work with directly. These sessions focus on problem-solving, system design, and mutual fit. They're a two-way conversation.",
  },
  {
    icon: Trophy,
    step: "04",
    title: "Offer",
    duration: "Within 1 week",
    description:
      "If it's a match, we move fast. You'll receive a written offer outlining compensation, equity where applicable, benefits, and start date.",
  },
];

export type Benefit = {
  icon: LucideIcon;
  title: string;
  description: string;
};

export const benefits: Benefit[] = [
  {
    icon: Banknote,
    title: "Competitive salary",
    description: "Market-rate compensation, benchmarked and reviewed annually.",
  },
  {
    icon: TrendingUp,
    title: "Equity",
    description: "Meaningful ownership for key contributors as Proxidize scales.",
  },
  {
    icon: Clock,
    title: "Flexible hours",
    description: "Own your schedule. Async-first culture focused on output, not hours logged.",
  },
  {
    icon: HeartHandshake,
    title: "Health & wellness",
    description: "Health coverage or a monthly wellness stipend, depending on your location.",
  },
  {
    icon: BadgeCheck,
    title: "Learning budget",
    description: "Annual budget for courses, books, conferences, and certifications.",
  },
  {
    icon: Globe2,
    title: "Paid time off",
    description: "Generous PTO including local public holidays and company-wide recharge days.",
  },
];
